import { Alert, Button, Center, Group, Loader, Paper, SimpleGrid, Skeleton, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import type { ReactNode } from 'react';

/**
 * Loading / empty / error views (§56). Every route with server data uses these
 * three plus its success state, so the app never shows a blank screen or a raw
 * error message (§55).
 */

export function LoadingState({ label = 'Загружаем...' }: { label?: string }) {
  return (
    <Center mih={220}>
      <Stack align="center" gap="sm">
        <Loader size="sm" />
        <Text size="sm" c="var(--text-muted)">
          {label}
        </Text>
      </Stack>
    </Center>
  );
}

/** Grid of card-shaped skeletons — used where the success state is a card grid. */
export function CardGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
      {Array.from({ length: count }, (_, index) => (
        <Paper key={index} p="lg" withBorder style={{ borderColor: 'var(--border-subtle)' }}>
          <Stack gap="sm">
            <Skeleton height={148} radius="md" />
            <Skeleton height={14} width="65%" radius="sm" />
            <Skeleton height={10} width="90%" radius="sm" />
            <Skeleton height={10} width="40%" radius="sm" />
          </Stack>
        </Paper>
      ))}
    </SimpleGrid>
  );
}

type EmptyStateProps = {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
};

export function EmptyState({ title, description, icon, action }: EmptyStateProps) {
  return (
    <Paper
      p="xl"
      radius="lg"
      withBorder
      style={{
        borderStyle: 'dashed',
        borderColor: 'var(--border-subtle)',
        background: 'var(--surface-elevated)',
      }}
    >
      <Stack align="center" gap="sm" py="lg">
        {icon ? <Group c="var(--text-subtle)">{icon}</Group> : null}
        <Title order={4} ta="center">
          {title}
        </Title>
        {description ? (
          <Text size="sm" c="var(--text-muted)" ta="center" maw={440}>
            {description}
          </Text>
        ) : null}
        {action ? <Group mt="xs">{action}</Group> : null}
      </Stack>
    </Paper>
  );
}

type ErrorStateProps = {
  title?: string;
  message?: string;
  onRetry?: () => void;
};

export function ErrorState({
  title = 'Не удалось загрузить данные',
  message = 'Проверьте соединение с сервером и попробуйте ещё раз.',
  onRetry,
}: ErrorStateProps) {
  return (
    <Alert
      variant="light"
      color="red"
      radius="lg"
      icon={<IconAlertTriangle size={18} />}
      title={title}
    >
      <Stack gap="sm" align="flex-start">
        <Text size="sm">{message}</Text>
        {onRetry ? (
          <Button
            size="xs"
            variant="light"
            color="red"
            leftSection={<IconRefresh size={14} />}
            onClick={onRetry}
          >
            Повторить
          </Button>
        ) : null}
      </Stack>
    </Alert>
  );
}
